import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';

import { Checkin } from 'libs/domain';
import { ReplaySubject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import scrollIntoView from 'scroll-into-view';

import { RootService } from '@app/src/app/minimal/services/RootService';

import * as formUtils from '@shared/components/crud-form-base/crud-form-base.utils';
import { CrudFormBase } from '@shared/components/crud-form-base/crud-form-base.abstract';
import { FormSelectOption } from '@shared/components/form';
import {
  MODAL_OVERLAY_DATA,
  ModalOverlayPrimaryHeaderBackground,
  ModalOverlayRef,
  ModalOverlayServiceCloseEventType,
} from '@shared/components/modal-overlay';
import { SystemAlertModalService } from '@shared/components/system-alert-modal';
import { CheckinService } from '@shared/services/checkin';

import { CheckinEditType } from '../../checkin-manager-reports.types';
import { CH_EDIT_FORMGROUP, CH_EDIT_FORM_FIELDS, ChReportMessages } from '../../constants';

const createForm = formUtils.createForm(CH_EDIT_FORMGROUP);
const setForm = formUtils.setForm(CH_EDIT_FORMGROUP);
const getData = formUtils.getData(CH_EDIT_FORMGROUP);

@Component({
  selector: 'mg-cm-history-edit',
  templateUrl: './checkin-manager-history-edit.component.html',
  styleUrls: ['./checkin-manager-history-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckinManagerHistoryEditComponent extends CrudFormBase<CheckinEditType> implements OnDestroy, OnInit {
  private _destroyed = new ReplaySubject<void>(1);

  public readonly form = this._fb.group(createForm());
  public FORM_FIELDS = CH_EDIT_FORM_FIELDS;
  public readonly MESSAGES = ChReportMessages;

  @Output() closeForm = new EventEmitter();

  public readonly checkinTypesOptions$ = this._cmService.getReasonsObs(true).pipe(
    takeUntil(this._destroyed),
    map(types =>
      types.map(type => ({
        label: type.name,
        value: type.id,
      })),
    ),
  );

  public readonly MODAL_CONFIG = {
    headerBg: ModalOverlayPrimaryHeaderBackground.TEAL,
  };

  @ViewChild('crudForm', { static: false })
  crudForm?: ElementRef<HTMLElement>;

  constructor(
    @Inject(MODAL_OVERLAY_DATA)
    private _dialogData: Checkin,
    private _fb: UntypedFormBuilder,
    public rootService: RootService,
    private _systemAlertModal: SystemAlertModalService,
    private _cmService: CheckinService,
    private _modalRef: ModalOverlayRef,
  ) {
    super({
      id: _dialogData?.id,
      get: async _data => _dialogData as Checkin,
      create: async data => data as Checkin,
      update: async data => {
        return this._cmService.updateCheckin(data as Checkin);
      },
      delete: async data => data as Checkin,
      onSetForm: data => {
        setForm(data, this.form);
      },
      onValidate: data => {
        formUtils.markNestedFormGroupAsDirty(this.form);
        return this.form.valid;
      },
      onSuccess: (type, data) => {
        this._modalRef.close(ModalOverlayServiceCloseEventType.SUBMIT, data);
      },
      onSubmit: data => {
        const mapped = getData(data, this.form);
        return mapped;
      },
      onShowLoader: promise => rootService.addLoadingPromise(promise),
      onCancel: async () => {
        this._modalRef.close(ModalOverlayServiceCloseEventType.CLOSE);
      },
      onDelete: async () => true,
      onFormStateChange: state => {
        if (state === 'invalid') {
          // scroll to top of form
          scrollIntoView(this.crudForm.nativeElement, {
            align: { top: 0 },
          });
        }
      },
    });
  }

  ngOnInit(): void {
    this.init();
  }

  ngOnDestroy() {
    this._destroyed.next();
    this._destroyed.complete();
  }

  updateTypeName(option: FormSelectOption) {
    this._dialogData.reasonName = option.label;
  }
}
